/**
 * Lightweight geolocation cache for the backoffice.
 *
 * The browser Geolocation API is permission-gated and slow on first call, so
 * we:
 *   1. Kick off a single getCurrentPosition() at app boot.
 *   2. Refresh in the background every 60s (cheaper after the first).
 *   3. Expose `getCurrentGeo()` — a synchronous read of the latest cached
 *      reading, used by the axios interceptor to attach `x-geo-lat`/`-lng`.
 *
 * If the user denies or the API is unavailable, every read returns null and
 * requests proceed without GPS — never block on geolocation.
 */
const CACHE_TTL_MS = 60_000;
const REFRESH_INTERVAL_MS = 60_000;

interface GeoReading {
  lat: number;
  lng: number;
  capturedAt: number;
}

let cached: GeoReading | null = null;
let started = false;

function readPosition(): Promise<GeoReading | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          lat: Number(pos.coords.latitude.toFixed(6)),
          lng: Number(pos.coords.longitude.toFixed(6)),
          capturedAt: Date.now(),
        });
      },
      () => resolve(null),
      // Keep timeouts modest — we never want to slow a request down.
      { enableHighAccuracy: false, timeout: 5_000, maximumAge: CACHE_TTL_MS },
    );
  });
}

export async function startGeolocationCapture(): Promise<void> {
  if (started) return;
  started = true;
  cached = await readPosition();
  // Best-effort background refresh.
  setInterval(async () => {
    const next = await readPosition();
    if (next) cached = next;
  }, REFRESH_INTERVAL_MS);
}

export function getCurrentGeo(): { lat: number; lng: number } | null {
  if (!cached) return null;
  if (Date.now() - cached.capturedAt > CACHE_TTL_MS * 5) return null;
  return { lat: cached.lat, lng: cached.lng };
}
